<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container justify-content-end">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>

    <div v-loading="isLoading">
      <div
        v-if="!isEditMode"
        class="mb-3"
      >
        <p class="font-size-14">{{ $t('hardcode.selectTheRuleType') }}</p>
        <el-radio-group v-model="selectedFormType">
          <el-radio
            v-for="form in forms"
            :key="form.refId"
            :label="form.refId"
          >{{ form.label }}</el-radio>
        </el-radio-group>
      </div>

      <transition
        name="fade"
        mode="out-in"
      >
        <template v-for="form in forms">
          <generic-form
            v-if="currentFormKey === form.refId"
            :key="form.refId"
            :ref="form.refId"
            :attributes="formAttributes"
            :form="formData"
            :form-template="form.template"
            class="pt-3"
          >
            <template v-for="(row, index) in form.template">
              <template v-if="row.isSlot">
                <el-form-item
                  :key="row.key"
                  :label="$t(`table.${row.key}`)"
                  :prop="row.key"
                  :rules="row.rules"
                  :style="{ order: index }"
                >
                  <store-selector
                    v-if="row.key === 'store_id'"
                    v-model="formData[row.key]"
                    :multiple="false"
                    class="w-100"
                    value-key="id"
                  />
                  <category-selector
                    v-else-if="row.key === 'categories'"
                    v-model="formData[row.key]"
                    :custom="{ 'multiple-limit': 2 }"
                  />
                  <brand-selector
                    v-else-if="row.key === 'brand'"
                    :multiple="false"
                    v-model="formData[row.key]"
                  />
                  <currency-selector
                    v-else-if="row.key === 'currency'"
                    v-model="formData[row.key]"
                  />
                </el-form-item>
              </template>
            </template>
          </generic-form>
        </template>
      </transition>
    </div>
  </sticky-header-layout>
</template>

<script>
import GenericForm from '../../../components/form/Form.vue'
import StoreSelector from '../../../components/selectors/StoreSelector.vue'
import CategorySelector from '../../../components/selectors/CategorySelector.vue'
import BrandSelector from '../../../components/selectors/BrandSelector.vue'
import CurrencySelector from '../../../components/selectors/CurrencySelector.vue'

import {
  hardcodeForms as forms,
  formDataMap,
} from '../../../const/product'
import DisplayHardcodeService from '../../../api/display-hardcode'
import HardcodeProductService from '../../../api/hardcode-product'

export default {
  name: 'HardcodeProductDetail',
  components: {
    GenericForm,
    BrandSelector,
    CategorySelector,
    StoreSelector,
    CurrencySelector,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const formIds = Object.keys(forms)
    const selectedFormType = 'productPrice'
    return {
      isLoading: false,
      isSaving: false,
      isEdited: false,

      forms,
      formIds,
      selectedFormType,
      currentFormKey: selectedFormType,
      formData: { type: forms[selectedFormType].type },
    }
  },
  computed: {
    formAttributes() {
      return {
        disabled: this.isSaving,
        labelWidth: '144px',
      }
    },
    isEditMode() {
      return !!this.id
    },
  },
  watch: {
    selectedFormType(v, prev) {
      if (this.currentFormKey === v) return

      let shouldSwitchForm = true
      if (Object.keys(this.formData).length > 1) {
        // if categories is empty list
        if (
          Object.keys(this.formData).length === 2
          && this.formData.categories
          && this.formData.categories.length === 0
        ) {
          // do nothing
        } else {
          shouldSwitchForm = false
        }
      }

      if (shouldSwitchForm) {
        this.switchForm(v)
      } else {
        this.$confirm('This will clear the data in current form, are you sure?')
          .then(() => {
            this.switchForm(v)
          })
          .catch(() => {
            this.selectedFormType = prev
          });
      }
    },
  },
  created() {
    if (this.isEditMode) {
      this.getDetail(this.id)
    }
  },
  methods: {
    // api calls
    async getDetail(id) {
      this.isLoading = true
      try {
        const { data } = await DisplayHardcodeService.getDetail(id)

        if (Array.isArray(data.location)) {
          data.location = data.location.join(',')
        }
        if (Array.isArray(data.target_id)) {
          data.target_id = data.target_id.join(',')
        }

        Object.keys(forms).forEach((key) => {
          if (forms[key].type === data.type) {
            this.selectedFormType = key
          }
        })
        this.$nextTick(() => {
          this.formData = data
        })
      } catch (error) {
        console.error(error)
        this.$alert(this.$t('error.hardcodeNotFound'), this.$t('common.error'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        }).then(() => {
          this.goToListPage()
        })
      } finally {
        this.isLoading = false
      }

      this.$nextTick(() => {
        this.isEdited = false
      })
    },
    async create(form) {
      this.isSaving = true
      try {
        if (form.type !== 999) {
          await DisplayHardcodeService.create(formDataMap(form))
        } else {
          const { data } = await HardcodeProductService.create({
            ...form,
            status: Number(form.status) ? 20 : 9, // 20: online, 9: offline
          })
          this.$message.success({
            message: this.$t('hardcode.productCreateSuccessfully', { productId: data.id }),
            duration: 0,
            showClose: true,
          })
        }
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async updateDetail(form) {
      console.log(form)
      this.isSaving = true
      try {
        await DisplayHardcodeService.update(formDataMap(form), this.id)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    // utils
    goToListPage() {
      this.$router.push({ path: '/product/hardcode/list' })
    },
    switchForm(formKey) {
      this.currentFormKey = formKey
      this.formData = {
        type: forms[formKey].type,
      }
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToListPage()
    },
    handleSaveFail(error) {
      console.error(error);
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    // handlers
    handleCancel() {
      this.$router.push({ path: '/product/hardcode/list' })
    },
    handleSave() {
      this.$refs[this.selectedFormType][0].$refs.form.validate().then(() => {
        if (this.forms[this.selectedFormType].type === 2) {
          if (!this.formData.store_id) {
            throw new Error()
          }
        }
        if (this.isEditMode) {
          this.updateDetail(this.formData)
        } else {
          this.create(this.formData)
        }
      }).catch(() => {
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";

$label-width: 88px;

.seo-link {
  text-decoration: underline;
}

::v-deep {
  fieldset {
    .el-form-item__label {
      width: $label-width !important;
    }
    .el-form-item__content {
      margin-left: $label-width !important;
    }
    .add-more-lang {
      margin-left: 28px;
    }
  }
}
</style>
